import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Edit2, Trash2, Check, X, Shield, Clock, AlertCircle } from 'lucide-react';
import { meetingsService } from '../meetings/meetingsService';
import { MeetingType } from '../meetings/types';
import { useTableSort, SortableTh } from '../../shared/useTableSort';

export const MeetingTypesWorkspace: React.FC = () => {
  const [types, setTypes] = useState<MeetingType[]>([]);
  const { sortedItems, sortField, sortDirection, toggleSort } = useTableSort(types);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal / Form state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<MeetingType | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    type_code: '',
    category: 'GOVERNANCE',
    default_duration_minutes: 60,
    requires_agenda: true,
    requires_minutes: true,
    requires_quorum: true,
    quorum_percentage: 50,
    is_management_review: false,
  });

  const loadTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await meetingsService.getMeetingTypes();
      setTypes(data || []);
    } catch (err: any) {
      setError(err.message || 'Toplantı türleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTypes();
  }, []);

  const openCreateModal = () => {
    setEditingType(null);
    setFormData({
      title: '',
      type_code: '',
      category: 'GOVERNANCE',
      default_duration_minutes: 60,
      requires_agenda: true,
      requires_minutes: true,
      requires_quorum: true,
      quorum_percentage: 50,
      is_management_review: false,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (t: MeetingType) => {
    setEditingType(t);
    setFormData({
      title: t.title,
      type_code: t.type_code,
      category: t.category || 'GOVERNANCE',
      default_duration_minutes: t.default_duration_minutes || 60,
      requires_agenda: Boolean(t.requires_agenda),
      requires_minutes: Boolean(t.requires_minutes),
      requires_quorum: Boolean(t.requires_quorum),
      quorum_percentage: t.quorum_percentage || 50,
      is_management_review: Boolean(t.is_management_review),
    });
    setIsModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setError('Lütfen toplantı türü başlığını girin.');
      return;
    }

    try {
      setError(null);
      if (editingType) {
        await meetingsService.updateMeetingType(editingType.id, {
          title: formData.title.trim(),
          category: formData.category,
          default_duration_minutes: Number(formData.default_duration_minutes),
          requires_agenda: formData.requires_agenda,
          requires_minutes: formData.requires_minutes,
          requires_quorum: formData.requires_quorum,
          quorum_percentage: Number(formData.quorum_percentage),
          is_management_review: formData.is_management_review,
        });
        setSuccess('Toplantı türü başarıyla güncellendi.');
      } else {
        const code = formData.type_code.trim() || formData.title.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').slice(0, 10);
        await meetingsService.createMeetingType({
          title: formData.title.trim(),
          type_code: code,
          category: formData.category,
          default_duration_minutes: Number(formData.default_duration_minutes),
          requires_agenda: formData.requires_agenda,
          requires_minutes: formData.requires_minutes,
          requires_quorum: formData.requires_quorum,
          quorum_percentage: Number(formData.quorum_percentage),
          is_management_review: formData.is_management_review,
        });
        setSuccess('Yeni toplantı türü sisteme kaydedildi.');
      }
      setIsModalOpen(false);
      await loadTypes();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      setError(err.message || 'İşlem sırasında hata oluştu.');
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (!window.confirm(`"${title}" toplantı türünü kaldırmak/devre dışı bırakmak istediğinize emin misiniz?`)) return;
    try {
      await meetingsService.deleteMeetingType(id);
      setSuccess(`"${title}" toplantı türü devre dışı bırakıldı.`);
      await loadTypes();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      setError(err.message || 'Silme işlemi başarısız oldu.');
    }
  };

  return (
    <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Calendar size={22} color="#2563eb" />
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a', margin: 0 }}>
              Meeting Types Management (Toplantı Türleri Yönetimi)
            </h2>
          </div>
          <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>
            SMS ve Kalite Yönetim Kurulları (SRB, SAG, Yönetim Gözden Geçirme vb.) toplantı türlerini ve kurallarını buradan yapılandırabilirsiniz.
          </p>
        </div>
        <button
          onClick={openCreateModal}
          style={{
            background: '#2563eb',
            color: '#ffffff',
            border: 'none',
            padding: '9px 16px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <Plus size={16} />
          + Yeni Toplantı Türü Ekle
        </button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #f87171', color: '#b91c1c', padding: '10px 14px', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {success && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', padding: '10px 14px', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Check size={16} />
          {success}
        </div>
      )}

      {/* Meeting Types Table */}
      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
          <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>
            <tr>
              <SortableTh field="title" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} style={{ padding: '12px 16px' }}>
                Tür Adı (Title)
              </SortableTh>
              <SortableTh field="type_code" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} style={{ padding: '12px 16px' }}>
                Kod (Code)
              </SortableTh>
              <SortableTh field="category" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} style={{ padding: '12px 16px' }}>
                Kategori
              </SortableTh>
              <SortableTh field="default_duration_minutes" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} style={{ padding: '12px 16px' }}>
                Süre
              </SortableTh>
              <SortableTh field="quorum_percentage" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} style={{ padding: '12px 16px' }}>
                Yeter Sayı (Quorum)
              </SortableTh>
              <SortableTh field="is_management_review" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} style={{ padding: '12px 16px' }}>
                Yönetim Gözden Geçirme
              </SortableTh>
              <th style={{ padding: '12px 16px', textAlign: 'right' }}>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
                  Toplantı türleri yükleniyor...
                </td>
              </tr>
            ) : sortedItems.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
                  Kayıtlı toplantı türü bulunamadı.
                </td>
              </tr>
            ) : (
              sortedItems.map((t) => (
                <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: '#1e293b' }}>
                    {t.title}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontFamily: 'monospace' }}>
                      {t.type_code}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', color: '#64748b' }}>
                    {t.category}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#64748b' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Clock size={13} color="#94a3b8" />
                      {t.default_duration_minutes || 60} dk
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {t.requires_quorum ? (
                      <span style={{ background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 500 }}>
                        Zorunlu (%{t.quorum_percentage || 50})
                      </span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '12px' }}>Gerekmiyor</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {t.is_management_review ? (
                      <span style={{ background: '#ede9fe', color: '#6d28d9', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 500 }}>
                        EASA / ISO MR
                      </span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '12px' }}>Normal Komite</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                      <button
                        onClick={() => openEditModal(t)}
                        title="Düzenle"
                        style={{ background: '#f1f5f9', border: 'none', borderRadius: '4px', padding: '6px', cursor: 'pointer', color: '#475569' }}
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(t.id, t.title)}
                        title="Devre Dışı Bırak"
                        style={{ background: '#fef2f2', border: 'none', borderRadius: '4px', padding: '6px', cursor: 'pointer', color: '#ef4444' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit Modal */}
      {isModalOpen && (
        <div className="modal-backdrop">
          <div className="modal-card max-w-lg">
            <div className="modal-header">
              <div className="modal-header-content">
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
                  {editingType ? 'Toplantı Türünü Düzenle' : 'Yeni Toplantı Türü Ekle'}
                </h3>
              </div>
              <button type="button" onClick={() => setIsModalOpen(false)} className="modal-close-btn">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSave}>
              <div className="modal-body space-y-4">
                <div className="form-field">
                  <label>Toplantı Türü Adı *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Örn: Fatigue Safety Action Group (FSAG)"
                    required
                  />
                </div>

                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Tür Kodu</label>
                    <input
                      type="text"
                      value={formData.type_code}
                      onChange={(e) => setFormData({ ...formData, type_code: e.target.value.toUpperCase() })}
                      placeholder="Örn: FSAG"
                    />
                  </div>
                  <div className="form-field">
                    <label>Kategori</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    >
                      <option value="GOVERNANCE">Governance & Oversight</option>
                      <option value="OPERATIONAL">Operational Safety</option>
                      <option value="TECHNICAL">Technical & Maintenance</option>
                      <option value="EXECUTIVE">Executive Management</option>
                    </select>
                  </div>
                </div>

                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Varsayılan Süre (Dakika)</label>
                    <input
                      type="number"
                      min="15"
                      max="480"
                      value={formData.default_duration_minutes}
                      onChange={(e) => setFormData({ ...formData, default_duration_minutes: Number(e.target.value) })}
                    />
                  </div>
                  <div className="form-field">
                    <label>Yeter Sayı Oranı (%)</label>
                    <input
                      type="number"
                      min="10"
                      max="100"
                      value={formData.quorum_percentage}
                      onChange={(e) => setFormData({ ...formData, quorum_percentage: Number(e.target.value) })}
                    />
                  </div>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer text-gray-700 font-medium">
                    <input
                      type="checkbox"
                      checked={formData.requires_quorum}
                      onChange={(e) => setFormData({ ...formData, requires_quorum: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Toplantı Başlangıcında Yeter Sayı (Quorum) Kontrolü Zorunlu
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-gray-700 font-medium">
                    <input
                      type="checkbox"
                      checked={formData.is_management_review}
                      onChange={(e) => setFormData({ ...formData, is_management_review: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Resmi EASA / ISO Yönetim Gözden Geçirme Oturumu Olarak İşaretle
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={() => setIsModalOpen(false)} className="btn-secondary">
                  İptal
                </button>
                <button type="submit" className="btn-primary">
                  {editingType ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
